import React, { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import TableLayout from "../../../components/TableLayout";
import dataHandler from "../../../hooks/dataHandler";
import { LOGAPIDATA } from "../../../services/ApiCalls";

const SystemNotification = () => {
  const [exportLoader, setExportLoader] = useState();

  const filterKeys = ["module", "action"];
  const {
    setBody,
    body,
    data,
    statusChangeHandler,
    loader,
    deleteModel,
    setDeleteModel,
    paginationHandler,
    searchHandler,
    total,
    deleteHandler,
  } = dataHandler({
    api: LOGAPIDATA,
    depenendencyArr: filterKeys,
  });

  const { t } = useTranslation();
  // System notifications data
  const systemNotifications = [
    {
      id: 1,
      event: "Payment Failure",
      trigger: "PG error",
      threshold: "Any failure",
      channels: ["Email", "In-App"],
      recipients: "Finance",
      status: "Active",
    },
    {
      id: 2,
      event: "Large Refund Volume",
      trigger: "Refund count",
      threshold: "≥ 20/day",
      channels: ["Email", "In-App"],
      recipients: "Finance",
      status: "Active",
    },
    {
      id: 3,
      event: "Settlement Batch Error",
      trigger: "Batch job fail",
      threshold: "Any error",
      channels: ["Email", "In-App"],
      recipients: "Finance",
      status: "Active",
    },
    {
      id: 4,
      event: "New Hotel Onboarding",
      trigger: "Signup request",
      threshold: "New hotel",
      channels: ["Email", "In-App"],
      recipients: "Operations",
      status: "Active",
    },
    {
      id: 5,
      event: "High Ticket Volume",
      trigger: "Support tickets",
      threshold: "≥ 100/3h",
      channels: ["Email", "In-App"],
      recipients: "Support",
      status: "Active",
    },
    {
      id: 6,
      event: "Low Inventory Alert",
      trigger: "Avail. rooms",
      threshold: "≤ 3 rooms",
      channels: ["Email", "In-App"],
      recipients: "Operations",
      status: "Paused",
    },
    {
      id: 7,
      event: "Super Admin Login",
      trigger: "Login event",
      threshold: "Any login",
      channels: ["Email", "In-App"],
      recipients: "Security",
      status: "Active",
    },
    {
      id: 8,
      event: "Irregular Activity",
      trigger: "Anomaly score",
      threshold: "≤ 0.9",
      channels: ["Email", "In-App"],
      recipients: "Security",
      status: "Active",
    },
    {
      id: 9,
      event: "API Key Rotated",
      trigger: "Key change",
      threshold: "Any change",
      channels: ["Email", "In-App"],
      recipients: "DevOps",
      status: "Active",
    },
    {
      id: 10,
      event: "Data Export Completed",
      trigger: "Export job",
      threshold: "Any job",
      channels: ["Email", "In-App"],
      recipients: "Ops + Finance",
      status: "Active",
    },
  ];

  const systemColumns = [
    {
      head: "#",
      accessor: "#",
      component: (item, key) => <>{key + 1}</>,
    },
    {
      head: "Event",
      accessor: "event",
      component: (item) => <span className="fw-semibold">{item.event}</span>,
    },
    {
      head: "Trigger",
      accessor: "trigger",
      component: (item) => <span>{item.trigger}</span>,
    },
    {
      head: "Threshold",
      accessor: "threshold",
      component: (item) => (
        <Badge bg="outline-secondary" className="text-dark border">
          {item.threshold}
        </Badge>
      ),
    },
    {
      head: "Channels",
      accessor: "channels",
      component: (item) => (
        <div className="d-flex gap-2">
          {item.channels.map((channel, idx) => (
            <Badge key={idx} bg="light" text="dark" className="border">
              {channel}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      head: "Recipients",
      accessor: "recipients",
      component: (item) => (
        <Badge bg="info" className="text-white">
          {item.recipients}
        </Badge>
      ),
    },
    {
      head: "Status",
      accessor: "status",
      component: (item) => (
        <Badge
          bg={item.status === "Active" ? "success" : "secondary"}
          className="text-white"
        >
          {item.status}
        </Badge>
      ),
    },
    {
      head: "Actions",
      accessor: "actions",
      component: (item) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(item)}
          >
            Edit
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleToggleStatus(item)}
          >
            {item.status === "Active" ? "Pause" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (notification) => {
    console.log("Edit notification:", notification);
    // Implement edit functionality
  };

  const handleToggleStatus = (notification) => {
    console.log("Toggle status for:", notification);
    // Implement status toggle functionality
  };

  return (
    <>
      <section className="position-relative py-2">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-bold text-dark">System Notification</h4>
              </div>
            </Col>

            {/* Filters Section */}
            <Col lg="12" className="my-3">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h6 className="fw-bold text-dark mb-3">Filters:</h6>
                  <Row className="g-3 align-items-end">
                    <Col md={2}>
                      <Form.Label className="fw-semibold text-muted mb-1">
                        Scope:
                      </Form.Label>
                      <Form.Select size="sm">
                        <option>All (TH + VN)</option>
                        <option>Thailand Only</option>
                        <option>Vietnam Only</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="fw-semibold text-muted mb-1">
                        Module:
                      </Form.Label>
                      <Form.Select size="sm">
                        <option>All</option>
                        <option>Finance</option>
                        <option>Operations</option>
                        <option>Security</option>
                        <option>Support</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="fw-semibold text-muted mb-1">
                        Severity:
                      </Form.Label>
                      <Form.Select size="sm">
                        <option>All</option>
                        <option>High</option>
                        <option>Medium</option>
                        <option>Low</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="fw-semibold text-muted mb-1">
                        Status:
                      </Form.Label>
                      <Form.Select size="sm">
                        <option>Active</option>
                        <option>Paused</option>
                        <option>Disabled</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="fw-semibold text-muted mb-1">
                        Recipient Group:
                      </Form.Label>
                      <Form.Select size="sm">
                        <option>All Groups</option>
                        <option>Finance</option>
                        <option>Operations</option>
                        <option>Security</option>
                        <option>Support</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="w-100"
                      >
                        Clear Filters
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* System Notifications Table */}
            <Col lg="12" className="my-3">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                  <TableLayout
                    column={systemColumns}
                    data={systemNotifications}
                    loader={loader}
                  />
                </Card.Body>
              </Card>
            </Col>

            {/* Notes Section */}
            {/* <Col lg="12" className="my-3">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h6 className="fw-bold text-dark mb-3">Notes</h6>
                  <p className="text-muted mb-0">
                    Configure internal alerts for Admin/Operations/Finance teams
                    (Email & In-App).
                  </p>
                </Card.Body>
              </Card>
            </Col> */}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default SystemNotification;
